import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  OnChangeFn,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { errorLogger } from '@/lib/errorLogger';

export type { ColumnDef };

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  density?: 'default' | 'compact';
  onRowClick?: (row: TData) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  className?: string;
  emptyMessage?: string;
  rowClassName?: (row: TData) => string | undefined;
}

const getAlignClass = (align?: 'left' | 'center' | 'right') => {
  switch (align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return 'text-left';
  }
};

type ColumnAlignMeta = { align?: 'left' | 'center' | 'right' };

class DataTableRowErrorBoundary extends React.Component<
  { colSpan: number; children: React.ReactNode },
  { hasError: boolean; errorId?: string }
> {
  state = { hasError: false, errorId: undefined as string | undefined };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const errorId = `datatable_row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.setState({ errorId });
    errorLogger.logError(error, undefined, {
      category: 'rendering',
      metadata: { errorId, scope: 'DataTableRow' },
      retryable: false,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <TableRow className="bg-warning-100/40">
          <TableCell colSpan={this.props.colSpan} className="py-3 text-sm text-text-secondary">
            Riga non caricata
            {import.meta.env.DEV && this.state.errorId ? ` (${this.state.errorId})` : null}
          </TableCell>
        </TableRow>
      );
    }

    return this.props.children;
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading,
  density = 'default',
  onRowClick,
  sorting,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection,
  columnVisibility,
  onColumnVisibilityChange,
  className,
  emptyMessage = 'Nessun dato disponibile',
  rowClassName,
}: DataTableProps<TData, TValue>) {
  // If sorting state is not controlled, we can use internal state if needed,
  // but for now we'll assume the parent controls it if they pass props.
  // Or we default to uncontrolled if props are undefined.
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const [internalColumnVisibility, setInternalColumnVisibility] = React.useState<VisibilityState>(
    {}
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: onSortingChange ?? setInternalSorting,
    onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,
    onColumnVisibilityChange: onColumnVisibilityChange ?? setInternalColumnVisibility,
    enableRowSelection,
    state: {
      sorting: sorting ?? internalSorting,
      rowSelection: rowSelection ?? internalRowSelection,
      columnVisibility: columnVisibility ?? internalColumnVisibility,
    },
  });

  if (loading) {
    const headerCount = Math.min(columns.length, 4);
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-md border border-[var(--border-default)] bg-base-white',
          className
        )}
      >
        <div className="border-b border-[var(--border-default)] bg-base-white px-4 py-3">
          <div className="flex gap-3">
            {Array.from({ length: headerCount }).map((_, idx) => (
              <Skeleton key={`header-skeleton-${idx}`} className="h-4 w-24" />
            ))}
          </div>
        </div>
        <div className="space-y-2 p-4">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={`row-skeleton-${idx}`} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const headClassName =
    density === 'compact' ? 'h-11 px-3 text-sm font-semibold text-text-primary' : undefined;
  const cellClassName = density === 'compact' ? 'px-3 py-2 text-sm' : undefined;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md border border-[var(--border-default)] bg-base-white',
        className
      )}
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const align = (header.column.columnDef.meta as ColumnAlignMeta | undefined)?.align;
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      getAlignClass(align),
                      headClassName,
                      header.column.getCanSort() && 'cursor-pointer select-none hover:bg-neutral-50'
                    )}
                    style={{
                      width: header.column.getSize(),
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex items-center gap-1.5',
                          align === 'right' && 'justify-end',
                          align === 'center' && 'justify-center'
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-text-tertiary">
                            {{
                              asc: <ArrowUp className="h-3.5 w-3.5" />,
                              desc: <ArrowDown className="h-3.5 w-3.5" />,
                            }[header.column.getIsSorted() as string] ?? (
                              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="[&_tr:last-child]:border-0">
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <DataTableRowErrorBoundary key={row.id} colSpan={columns.length}>
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    onRowClick && 'cursor-pointer hover:bg-neutral-50',
                    row.getIsSelected() && 'bg-brand-blue-50 hover:bg-brand-blue-100',
                    rowClassName?.(row.original)
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const align = (cell.column.columnDef.meta as ColumnAlignMeta | undefined)
                      ?.align;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(getAlignClass(align), cellClassName)}
                        style={{
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </DataTableRowErrorBoundary>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-text-secondary">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
